<script>
import { AbcToggle } from "@ordbogen-npm/abc-dsk";
import { debounce } from "lodash-es";
import { mapStores } from "pinia";
import { defineComponent } from "vue";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import TgSavedToast from "@/abc-dsk/components/SavedToast/TgSavedToast.vue";
import TgSelectorButton from "@/abc-dsk/components/SelectorButton/TgSelectorButton.vue";
import TgSelectorButtonGroup from "@/abc-dsk/components/SelectorButton/TgSelectorButtonGroup.vue";
import { getTranslatedLanguageGroups } from "@/common/language-group-helpers";
import { LANGUAGE_GROUPS_PRIORITIZED } from "@/common/language-groups";
import { LEVELS } from "@/common/level";
import { PACKAGE_GROUP_GYLDENDAL } from "@/common/package-groups-types";
import ImageResponsive from "@/components/ImageResponsive.vue";
import { MatomoHelper } from "@/helpers";
import { useAuthStore, useLanguageLevelStore, useSettingsStore } from "@/stores";
import { MatomoEventAction, MatomoEventCategory } from "@/types";

export default defineComponent({
  name: "GyldendalSettings",
  components: {
    AbcToggle,
    ImageResponsive,
    TgButton,
    TgSavedToast,
    TgSelectorButton,
    TgSelectorButtonGroup
  },
  data() {
    return {
      levels: LEVELS,
      languageNames: getTranslatedLanguageGroups(),
      languagesUpdatedMap: LANGUAGE_GROUPS_PRIORITIZED.reduce((map, l) => {
        map[l] = false;
        return map;
      }, {}),
      levelNames: this.$tm("levels"),
      autoExpandArticlesLabels: this.$tm("settings.gyldendal.autoExpandArticlesLabels"),
      updatedAutoExpandArticlesToast: false
    };
  },
  computed: {
    ...mapStores(useAuthStore, useLanguageLevelStore, useSettingsStore),

    languageLevels() {
      return this["language-levelStore"].gyldendalLanguageLevels;
    },

    autoExpandArticles() {
      return this.settingsStore.userPreferences.gyldendalArticleTranslationsExpandedDefault;
    },
    autoExpandArticlesSubheading() {
      return this.$rt(this.autoExpandArticlesLabels[this.authStore.user.level]);
    }
  },
  methods: {
    setLanguageUpdated(language, value = true) {
      this.languagesUpdatedMap[language] = value;
    },

    async setLevel(language, level) {
      this.setLanguageUpdated(language, false);

      await this["language-levelStore"].updateLanguageLevel({
        languageGroup: language,
        level,
        packageGroup: PACKAGE_GROUP_GYLDENDAL
      });

      this.setLanguageUpdated(language, true);

      MatomoHelper.sendEvent(
        MatomoEventCategory.AbcSettings,
        MatomoEventAction.ClickGdSettings,
        `Changed ${language}(language) to ${level} level`
      );
    },

    resetLevel: debounce(
      async function () {
        LANGUAGE_GROUPS_PRIORITIZED.forEach((l) => this.setLanguageUpdated(l, false));

        await this["language-levelStore"].resetAllLanguageLevels(PACKAGE_GROUP_GYLDENDAL);

        LANGUAGE_GROUPS_PRIORITIZED.forEach((l) => this.setLanguageUpdated(l));
      },
      500,
      { leading: true, trailing: false }
    ),

    async toggleAutoExpandArticles(value) {
      this.updatedAutoExpandArticlesToast = false;

      await this.settingsStore.setGyldenalArticleTranslationsExpandedDefault(value);

      this.updatedAutoExpandArticlesToast = true;

      MatomoHelper.sendEvent(
        MatomoEventCategory.AbcSettings,
        MatomoEventAction.ClickGdSettings,
        `Toggled auto unfold to ${value}`
      );
    }
  }
});
</script>

<template>
  <div>
    <div class="d-none d-lg-block settings-group">
      <h2 class="settings-group__heading settings-group__heading--big">
        {{ $t("settings.gyldendal.title") }}
      </h2>
      <p class="settings-group__subheading mb-0">
        {{ $t("settings.gyldendal.description") }}
      </p>
    </div>
    <hr class="settings-group-divider d-none d-lg-block" />
    <div class="d-block d-lg-none settings-group settings-group--even-spacing pb-0 row">
      <div class="col">
        <p class="settings-group__subheading mb-n2">
          {{ $t("settings.gyldendal.description") }}
        </p>
      </div>
    </div>

    <template v-for="(group, index) in languageLevels" :key="index">
      <div class="settings-group row">
        <div class="col-12 col-lg-5 d-flex">
          <div class="settings-group__content settings-group__content--horizontal-lg">
            <ImageResponsive
              :src="`/assets/img/settings/flags/${group.language}@1x.png`"
              presentational
              class="mr-3"
            />
            <h3 class="settings-group__heading settings-group__heading--depressed">
              {{ languageNames[group.language] }}
            </h3>
          </div>
          <div class="settings-group__toast-container settings-group__toast-container--centered-lg">
            <TgSavedToast v-model:show="languagesUpdatedMap[group.language]" :left="-67" absolute />
          </div>
        </div>
        <div class="col-12 col-lg-7">
          <TgSelectorButtonGroup
            v-slot="{ state, handleBlur, handleFocusNext, handleFocusPrev, handleSelect }"
            :selected="group.level"
            :items="levels"
            class="row no-gutters"
            @input="setLevel(group.language, $event.item)"
          >
            <TgSelectorButton
              v-for="(level, levelIndex) in levels"
              :key="`level-${levelIndex}`"
              :index="levelIndex"
              :tabbable="state.tabbableIndex === levelIndex"
              :selected="state.selectedIndex === levelIndex"
              :aria-label="
                $t('settings.gyldendal.buttonLabel', {
                  language: languageNames[group.language],
                  level: $rt(levelNames[level])
                })
              "
              class="col"
              :class="{ 'mx-3': levelIndex === 1 }"
              color="secondary"
              size="sm"
              @blur="handleBlur($event)"
              @select="handleSelect($event)"
              @focus-next="handleFocusNext($event)"
              @focus-prev="handleFocusPrev($event)"
            >
              {{ $rt(levelNames[level]) }}
            </TgSelectorButton>
          </TgSelectorButtonGroup>
        </div>
      </div>
      <hr class="settings-group-divider" />
    </template>

    <div class="settings-group settings-group--even-spacing row">
      <div class="col-12">
        <TgButton
          variant="outline"
          size="sm"
          color="yellow"
          :aria-label="$t('settings.gyldendal.resetLabel')"
          @click="resetLevel()"
        >
          {{ $t("settings.gyldendal.reset") }}
        </TgButton>
      </div>
    </div>
  </div>

  <hr class="settings-group-divider" />
  <div class="settings-group row">
    <div class="col-12 settings-group__content">
      <div class="settings-group__details">
        <h3 class="settings-group__heading">
          {{ $t("settings.ordbogen.autoExpandArticlesHeading") }}
        </h3>
        <p class="settings-group__subheading">{{ autoExpandArticlesSubheading }}</p>
      </div>
    </div>
    <div class="col-12 mb-3">
      <div class="settings-group__toast-container settings-group__toast-container--centered">
        <AbcToggle
          :model-value="autoExpandArticles"
          :aria-label="$t('settings.gyldendal.autoExpandArticlesHeading')"
          @update:model-value="toggleAutoExpandArticles($event)"
        />
        <TgSavedToast v-model:show="updatedAutoExpandArticlesToast" :top="0" :left="60" absolute />
      </div>
    </div>
  </div>
</template>
